import { useEffect, useId, useState } from 'react'
import { createPortal } from 'react-dom'
import { X } from 'lucide-react'
import { strings } from '@/strings'

const ALT_TEXT_MAX = 2000

interface AltTextModalProps {
    imagePreview: string
    draft: string
    onDraftChange: (value: string) => void
    onSave: () => void
    onClose: () => void
}

export function AltTextModal({
    imagePreview,
    draft,
    onDraftChange,
    onSave,
    onClose,
}: AltTextModalProps) {
    const altRemaining = ALT_TEXT_MAX - draft.length
    const titleId = useId()
    // Lazy initializer runs during render, before the textarea's autoFocus steals focus in the
    // commit phase — capturing in an effect would be too late and would just see the textarea.
    const [previouslyFocused] = useState<HTMLElement | null>(
        () => document.activeElement as HTMLElement | null
    )

    useEffect(() => {
        return () => {
            // The trigger element may have been removed from the DOM (e.g. the edited image
            // was removed while the modal was open) — focusing a detached element is a no-op.
            if (previouslyFocused?.isConnected) {
                previouslyFocused.focus()
            }
        }
    }, [previouslyFocused])

    return createPortal(
        <>
            <div className="fixed inset-0 bg-black/50 z-[60]" onClick={onClose} />
            <div className="fixed inset-0 z-[61] flex items-center justify-center p-4">
                <div
                    role="dialog"
                    aria-modal="true"
                    aria-labelledby={titleId}
                    className="bg-surface-light dark:bg-surface-dark border border-border-light dark:border-border-dark rounded-2xl shadow-2xl w-full max-w-lg overflow-hidden flex flex-col max-h-[90vh]"
                >
                    {/* Header */}
                    <div className="flex items-center justify-between px-4 py-3 border-b border-border-light dark:border-border-dark">
                        <h3
                            id={titleId}
                            className="text-base font-bold text-text-primary-light dark:text-text-primary-dark"
                        >
                            {strings.altText.title}
                        </h3>
                        <button
                            type="button"
                            onClick={onClose}
                            className="p-1 text-text-secondary-light dark:text-text-secondary-dark hover:opacity-70 transition-opacity"
                        >
                            <X className="w-5 h-5" />
                        </button>
                    </div>

                    {/* Image preview */}
                    <div className="px-4 pt-4 overflow-y-auto">
                        <img
                            src={imagePreview}
                            alt={strings.altText.previewAlt}
                            className="w-full max-h-64 object-contain rounded-lg border border-border-light dark:border-border-dark"
                        />
                    </div>

                    {/* Alt text input */}
                    <div className="px-4 pt-3 pb-2">
                        <label
                            htmlFor="alt-text-input"
                            className="block text-sm text-text-secondary-light dark:text-text-secondary-dark mb-1"
                        >
                            {strings.altText.fieldLabel}
                        </label>
                        <textarea
                            id="alt-text-input"
                            value={draft}
                            onChange={e => onDraftChange(e.target.value)}
                            placeholder={strings.altText.placeholder}
                            maxLength={ALT_TEXT_MAX}
                            rows={3}
                            className="w-full text-base px-3 py-2 rounded-lg border border-border-light dark:border-border-dark bg-transparent text-text-primary-light dark:text-text-primary-dark placeholder:text-text-secondary-light dark:placeholder:text-text-secondary-dark focus:outline-none focus:ring-1 focus:ring-primary resize-none"
                            autoFocus
                        />
                    </div>

                    {/* Footer */}
                    <div className="flex items-center justify-between px-4 py-3 border-t border-border-light dark:border-border-dark">
                        <span
                            className={`text-sm ${altRemaining < 100 ? 'text-yellow-500' : 'text-text-secondary-light dark:text-text-secondary-dark'}`}
                        >
                            {altRemaining}
                        </span>
                        <button
                            type="button"
                            onClick={onSave}
                            className="px-6 py-2 bg-primary text-white text-sm font-semibold rounded-full hover:opacity-90 transition-opacity"
                        >
                            {strings.common.save}
                        </button>
                    </div>
                </div>
            </div>
        </>,
        document.body
    )
}
